// Date rules for field visit requests. They mirror FieldVisitRequest in the
// API so the form can flag problems before submitting; the API stays the
// source of truth.

// Minimum days between the submission date and the first visit day, unless
// the submitter uses the short-notice override.
export const MINIMUM_NOTICE_DAYS = 28;

// Samples must be in-house this many days before the first visit day.
export const SAMPLES_LEAD_DAYS = 7;

// The API dates submissions in Xceed HQ time, not the rep's time zone.
export const SUBMISSION_TIME_ZONE = 'America/New_York';

export interface VisitDateRange {
  start_date: string;
  end_date?: string | null;
}

const hqDateParts = new Intl.DateTimeFormat('en-US', {
  timeZone: SUBMISSION_TIME_ZONE,
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
});

// Today's date (YYYY-MM-DD) in HQ time. Built from the parts rather than a
// locale's formatted string, so it doesn't depend on how a locale orders them.
export const submissionDate = (now: Date = new Date()): string => {
  const parts = hqDateParts.formatToParts(now);
  const part = (type: Intl.DateTimeFormatPartTypes) => parts.find((p) => p.type === type)?.value ?? '';
  return `${part('year')}-${part('month')}-${part('day')}`;
};

// Calendar arithmetic on ISO dates, done in UTC so DST never shifts a day.
export const addDays = (isoDate: string, days: number): string => {
  const date = new Date(`${isoDate}T00:00:00Z`);
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
};

export const daysBetween = (fromIso: string, toIso: string): number =>
  Math.round(
    (Date.parse(`${toIso}T00:00:00Z`) - Date.parse(`${fromIso}T00:00:00Z`)) / 86_400_000,
  );

// The first day a visit may start without the override.
export const earliestVisitDate = (submittedOn: string): string =>
  addDays(submittedOn, MINIMUM_NOTICE_DAYS);

// The earliest start date across the rows that have one. ISO dates sort as
// strings.
export const firstVisitDay = (ranges: VisitDateRange[]): string | null => {
  const starts = ranges.map((r) => r.start_date).filter(Boolean).sort();
  return starts[0] ?? null;
};

export const meetsMinimumNotice = (submittedOn: string, firstDay: string): boolean =>
  daysBetween(submittedOn, firstDay) >= MINIMUM_NOTICE_DAYS;

export const samplesDueBy = (firstDay: string): string => addDays(firstDay, -SAMPLES_LEAD_DAYS);

// "2026-12-01" -> "Tue, Dec 1, 2026", without a time zone shift.
export const formatVisitDate = (isoDate: string): string =>
  new Date(`${isoDate}T00:00:00Z`).toLocaleDateString('en-US', {
    timeZone: 'UTC',
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  });
